import { useState, useCallback, useRef, useEffect } from "react"
import type { DiscoverFilters, DiscoverResults } from "~/react/types/discover"
import { discovers_path } from "~/lib/routes"
import { getCsrfToken } from "~/lib/utils"
import { buildFilterParams, hasActiveFilters } from "~/react/utils/discoverFilters"

interface UseDiscoverResultsProps {
  initialResults: DiscoverResults | null
  initialHasFilters: boolean
}

export function useDiscoverResults({ initialResults, initialHasFilters }: UseDiscoverResultsProps) {
  const [results, setResults] = useState<DiscoverResults | null>(initialResults)
  const [isLoading, setIsLoading] = useState(false)
  const [hasFilters, setHasFilters] = useState(initialHasFilters)
  const abortControllerRef = useRef<AbortController | null>(null)

  // Cleanup on unmount - abort any in-flight requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const fetchResults = useCallback(async (filters: DiscoverFilters, offset?: number) => {
    // Cancel any in-flight request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    // Build query string from filters
    const params = buildFilterParams(filters)

    // Add offset for pagination
    if (offset && offset > 0) {
      params.set("offset", offset.toString())
    }

    const newHasFilters = hasActiveFilters(filters)

    // Update URL without reload
    const queryString = params.toString()
    const url = queryString ? `${discovers_path()}?${queryString}` : discovers_path()
    window.history.replaceState(null, "", url)

    // If no filters, clear results
    if (!newHasFilters) {
      setResults(null)
      setHasFilters(false)
      return
    }

    setIsLoading(true)
    setHasFilters(true)

    try {
      // Add format=json to get JSON response
      params.append("format", "json")

      const response = await fetch(`${discovers_path()}?${params.toString()}`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        signal: abortControllerRef.current.signal,
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      setResults(data.results)
    } catch (error) {
      // Ignore abort errors - they're expected when a new request cancels the previous one
      if (error instanceof Error && error.name === "AbortError") {
        return
      }
      console.error("Failed to fetch discover results:", error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  const clearResults = useCallback(() => {
    setResults(null)
    setHasFilters(false)
    window.history.replaceState(null, "", discovers_path())
  }, [])

  return {
    results,
    isLoading,
    hasFilters,
    fetchResults,
    clearResults,
  }
}
